import moment from 'moment';
import validate from 'validate.js';
import Translation from 'lib/helpers/translation';
import {dateIsValid, portCodePattern} from 'ferries/helpers/common';
import {today, afterOneYear, validateDatetime} from 'helpers/time';

validate.validators.inequality = function (value, options, attribute, attributes) {
  if (!validate.isDefined(value)) {
    return;
  }

  if (validate.isString(options)) {
    options = {attribute: options};
  }
  options = validate.extend({}, this.options, options);
  const message = options.message ||
    this.message ||
    'should not be equal to %{attribute}';

  if (validate.isEmpty(options.attribute) || !validate.isString(options.attribute)) {
    throw new Error('The attribute must be a non empty string');
  }

  const otherValue = validate.getDeepObjectValue(attributes, options.attribute);
  const comparator = options.comparator || function (v1, v2) {
    return v1 !== v2;
  };

  if (!comparator(value, otherValue, options, attribute, attributes)) {
    return validate.format(message, {attribute: validate.prettify(options.attribute)});
  }
};

validate.validators.notBefore = function (value, options, attribute, attributes) {
  if (!validate.isDefined(value)) {
    return;
  }

  if (validate.isString(options)) {
    options = {attribute: options};
  }
  options = validate.extend({}, this.options, options);
  const message = options.message ||
    this.message ||
    'should not be before %{attribute}';

  if (validate.isEmpty(options.attribute) || !validate.isString(options.attribute)) {
    throw new Error('The attribute must be a non empty string');
  }

  const otherValue = validate.getDeepObjectValue(attributes, options.attribute);
  const comparator =
    options.comparator
    || function (v1, v2) {
      return moment(v1).isAfter(moment(v2));
    };

  if (comparator(value, otherValue, options, attribute, attributes)) {
    return validate.format(message, {attribute: validate.prettify(options.attribute)});
  }
};

validate.validators.portCode = function (value, options) {
  if (!validate.isDefined(value)) {
    return;
  }

  if (validate.isString(options)) {
    options = {attribute: options};
  }

  options = validate.extend({}, this.options, options);
  const message =
    options.message ||
    this.message ||
    'should be more than 3 letters or them into parentheses';

  if (value.length >= 3) {
    if (!value.match(options.pattern)) {
      return validate.format(message, {value: options.value});
    }
  }
};

validate.validators.portCombination = function (value, options) {
  const {depPort, arrPort} = options;

  if (!depPort?.dependentPorts?.includes(arrPort?.ferryOwnLocationID)) {
    return options.message;
  }
};

validate.extend(validate.validators.datetime, validateDatetime);

validate.validators.type = function (value, options) {
  if (typeof options === 'object' && options.clazz) {
    return value instanceof options.clazz ? null : ' is not of type "' + options.clazz.name + '"';
  }

  if (!validate.validators.type.checks[options]) {
    throw new Error('Could not find validator for type ' + options);
  }
  return validate.validators.type.checks[options](value) ? null : ' is not of type "' + options + '"';
};

validate.validators.type.checks = {
  Object: function (value) {
    return validate.isObject(value) && !validate.isArray(value);
  },
  Array: validate.isArray,
  Integer: validate.isInteger,
  Number: validate.isNumber,
  String: validate.isString,
  Date: validate.isDate,
  Boolean: validate.isBoolean
};

const searchTripConstraints = (depPort, arrPort) => ({
  dep: {
    presence: {
      message: Translation.tr('depPortEmpty', 'searchMaskErrors'),
      allowEmpty: false
    },
    type: 'String',
    length: {
      minimum: 3,
      message: Translation.tr('depPortInvalid', 'searchMaskErrors')
    },
    inequality: {
      attribute: 'arr',
      message: Translation.tr('depArrPortsIdentical', 'searchMaskErrors')
    },
    portCode: {
      pattern: portCodePattern,
      message: Translation.tr('depPortInvalid', 'searchMaskErrors')
    },
    portCombination: {
      depPort,
      arrPort,
      message: Translation.tr('portCombinationInvalid', 'searchMaskErrors')
    }
  },
  arr: {
    presence: {
      message: Translation.tr('arrPortEmpty', 'searchMaskErrors'),
      allowEmpty: false
    },
    type: 'String',
    length: {
      minimum: 3,
      message: Translation.tr('arrPortInvalid', 'searchMaskErrors')
    },
    inequality: {
      attribute: 'dep',
      message: Translation.tr('depArrPortsIdentical', 'searchMaskErrors')
    },
    portCode: {
      pattern: portCodePattern,
      message: Translation.tr('arrPortInvalid', 'searchMaskErrors')
    }
  },
  obDate: {
    presence: {
      message: Translation.tr('at_obDateInvalid', 'searchMaskErrors')
    },
    datetime: {
      latest: afterOneYear,
      earliest: today,
      message: Translation.tr('at_ibDateInPast', 'searchMaskErrors')
    },
    notBefore: {
      attribute: 'ibDate',
      message: Translation.tr('at_ibDateSmallerThanObDate', 'searchMaskErrors')
    }
  },
  ibDate: {
    presence: {
      message: Translation.tr('at_ibDateInvalid', 'searchMaskErrors')
    },
    datetime: {
      latest: afterOneYear,
      earliest: today,
      message: Translation.tr('at_ibDateInPast', 'searchMaskErrors')
    }
  }
});

// const searchParamsConstraints = {
//   typeTrip: {
//     presence: true,
//     type: 'Number'
//   },
//   passengers: {
//     presence: {
//       message: Translation.tr('at_passengersAdultNotNumber', 'searchMaskErrors')
//     },
//     numericality: {
//       noStrings: true,
//       onlyInteger: true,
//       greaterThanOrEqualTo: 1,
//       lessThanOrEqualTo: 9,
//       message: Translation.tr('at_passengersAdultNotInRange', 'searchMaskErrors')
//     }
//   },
//   vehicles: {
//     presence: {
//       message: Translation.tr('at_passengersChildNotNumber', 'searchMaskErrors')
//     },
//     numericality: {
//       noStrings: true,
//       onlyInteger: true,
//       greaterThanOrEqualTo: 0,
//       lessThanOrEqualTo: 4,
//       message: Translation.tr('at_passengersChildNotInRange', 'searchMaskErrors')
//     }
//   }
// };

const validateQuery = (query) => (
  new Promise((resolve, reject) => {
    const errors = [];

    query.trips.forEach((trip) => {
      const validateTrip = validate(
        trip,
        searchTripConstraints(trip.depSuggestion, trip.arrSuggestion),
        {fullMessages: false}
      );
      errors.push(validateTrip);
    });

    if (errors.filter((error) => Boolean(error)).length > 0) {
      return reject({type: 'validation errors', errors});
    }
    resolve(query);
  })
);

const validateLocation = (locations) => {
  if (!Array.isArray(locations)) {
    return false;
  }

  return locations.reduce((acc, value) =>
    acc && (value && value.length >= 3), true);
};

const isQueryValid = ({dep, arr, date}) => {
  if (!validateLocation(dep)) {
    return false;
  }
  if (!validateLocation(arr)) {
    return false;
  }

  if (dep.length !== arr.length || dep.length !== date.length) {
    return false;
  }

  if (!dateIsValid(date)) {
    return false;
  }

  return true;
};

export {validateQuery, isQueryValid};